import { tx as translateInterfaceText } from '@/utils/i18n';

import { VideoFeatures } from '@/store/videoFeatures';
import { FeatureItemInfo, FeatureCategoryInfo } from './types';
import { featureDefinitions } from './featureData';
import { isFeatureConfigured } from './featureConfig';
import { Brush, Play, ShoppingCart } from 'lucide-react';
import React from 'react';

const categoryMetadata: Record<string, { title: string; icon: React.ReactNode }> = {
  'player':     { get title() { return translateInterfaceText("PLAYER"); },    icon: <Play className="h-3 w-3" /> },
  'visual':     { get title() { return translateInterfaceText("VISUAL"); },    icon: <Brush className="h-3 w-3" /> },
  'conversion': { get title() { return translateInterfaceText("CONVERSÃO"); }, icon: <ShoppingCart className="h-3 w-3" /> },
};

export const getCategoryTitle = (categoryId: string): string => {
  return categoryMetadata[categoryId]?.title || categoryId;
};

export const getCategoryIcons = (categoryId: string): React.ReactNode => {
  return categoryMetadata[categoryId]?.icon || null;
};

export const getFeatureCategories = (features?: VideoFeatures): FeatureCategoryInfo[] => {
  if (!featureDefinitions || !Array.isArray(featureDefinitions)) {
    console.error('[getFeatureCategories] featureDefinitions is undefined or not an array');
    return [];
  }

  const order = ['player', 'visual', 'conversion'] as const;
  const categories: Record<string, FeatureCategoryInfo> = {};
  order.forEach(id => {
    categories[id] = {
      id,
      title: getCategoryTitle(id),
      icon: categoryMetadata[id].icon,
      items: [],
    };
  });

  const sortedFeatures = [...featureDefinitions].sort(
    (a, b) => (a.order || Infinity) - (b.order || Infinity)
  );

  sortedFeatures.forEach(feature => {
    if (!feature || typeof feature !== 'object') return;
    const cat = categories[feature.category as string];
    if (!cat) {
      console.warn('[getFeatureCategories] Invalid category for feature:', feature.id, feature.category);
      return;
    }
    cat.items.push({
      id: feature.id,
      name: feature.title,
      icon: feature.icon,
      isNew: feature.isNew,
      isPopular: feature.isPopular,
      isPremium: feature.isPremium,
      isConfigured: features ? () => isFeatureConfigured(features, feature.id) : () => false,
    });
  });

  return order.map(id => categories[id]).filter(cat => cat.items.length > 0);
};

export const getFeatureItems = (features?: VideoFeatures): FeatureItemInfo[] => {
  if (!featureDefinitions || !Array.isArray(featureDefinitions)) {
    console.error('[getFeatureItems] featureDefinitions is undefined or not an array');
    return [];
  }

  const items = featureDefinitions.map(feature => {
    if (!feature || typeof feature !== 'object') return null;

    return {
      id: feature.id,
      name: feature.title,
      icon: feature.icon,
      isNew: feature.isNew,
      isPopular: feature.isPopular,
      isPremium: feature.isPremium,
      isConfigured: features ? () => isFeatureConfigured(features, feature.id) : () => false,
    };
  }).filter(Boolean);

  return Array.isArray(items) ? items : [];
};
